import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { BankLinkReuseSource, BankLinkWallet } from '@/util/bank-link';

export type FundingBankReplaceContextValue = {
  walletFor: (accountId: string) => BankLinkWallet | undefined;
  pendingSources: readonly BankLinkReuseSource[];
  onUpdated: () => void;
  /** Latest request to open an account's funding bank popover (e.g. Deposit with no bank). */
  openRequest: { accountId: string; nonce: number } | null;
  requestOpen: (accountId: string) => void;
};

const FundingBankReplaceContext = createContext<FundingBankReplaceContextValue | null>(null);

type FundingBankReplaceProviderProps = {
  wallets: readonly BankLinkWallet[];
  pendingSources: readonly BankLinkReuseSource[];
  onUpdated: () => void;
  children: React.ReactNode;
};

export const FundingBankReplaceProvider: React.FC<FundingBankReplaceProviderProps> = ({
  wallets,
  pendingSources,
  onUpdated,
  children,
}) => {
  const [openRequest, setOpenRequest] = useState<{ accountId: string; nonce: number } | null>(
    null,
  );
  const requestOpen = useCallback((accountId: string) => {
    setOpenRequest((previous) => ({ accountId, nonce: (previous?.nonce ?? 0) + 1 }));
  }, []);
  const value = useMemo<FundingBankReplaceContextValue>(() => {
    const walletsById = new Map(wallets.map((wallet) => [wallet.account_id, wallet]));
    return {
      walletFor: (accountId: string) => walletsById.get(accountId),
      pendingSources,
      onUpdated,
      openRequest,
      requestOpen,
    };
  }, [wallets, pendingSources, onUpdated, openRequest, requestOpen]);

  return (
    <FundingBankReplaceContext.Provider value={value}>{children}</FundingBankReplaceContext.Provider>
  );
};

export const useFundingBankReplace = (): FundingBankReplaceContextValue =>
  useContext(FundingBankReplaceContext) ?? {
    walletFor: () => undefined,
    pendingSources: [],
    onUpdated: () => undefined,
    openRequest: null,
    requestOpen: () => undefined,
  };
